<template>
  <BlockFieldset class="project-presentations">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <b-row v-else>
      <b-col lg="8" md="8" sm="12">
        <div v-if="isShortTeaserVisible" class="presentations-part-short-teaser">
          <legend>
            <strong>{{ $t('shortTeaser') }}: {{ $t('generate_report') }}</strong>
          </legend>
          <b-row>
            <b-col lg="4" md="7" sm="7">
              <AttachmentList
                :attachments="shortTeaserAttachments"
                :attachmentType="shortTeaserAttachmentType"
                :deletable="!isBlockReadOnly"
                :hidesDeleteForGuests="!isOrganizationRoleNavigationEnabled"
                v-on:attachment-list:delete="onDeleteAttachment"
              />
            </b-col>
            <b-col lg="8" md="5" sm="5">
              <router-link
                class="btn btn-success pull-right"
                :to="{ name: 'ShortTeaserPrintPage', params: { projectNumber: project.number } }"
                target="_blank"
              >
                <i class="fa fa-file mr-2"></i>{{ $t('shortTeaserPreview') }}
              </router-link>
              <ButtonWait
                v-if="canChangeAttachments"
                :startCallback="startShortTeaserBuild"
                :pollCallback="pollShortTeaserBuildState"
                class="pull-right mr-2"
              >
                <template v-slot:buttonText>
                  {{ $t('generateShortTeaser') }}
                </template>
              </ButtonWait>
            </b-col>
          </b-row>
        </div>

        <div v-if="isMasterPresentationVisible" class="presentations-part-master-presentation">
          <legend class="mt-3">
            <strong>{{ $t('masterPresentation') }}: {{ $t('generate_report') }}</strong>
          </legend>
          <b-row>
            <b-col lg="4" md="7" sm="7">
              <AttachmentList
                :attachments="masterPresentationAttachments"
                :attachmentType="masterPresentationAttachmentType"
                :deletable="!isBlockReadOnly"
                :hidesDeleteForGuests="!isOrganizationRoleNavigationEnabled"
                v-on:attachment-list:delete="onDeleteAttachment"
              />
            </b-col>
            <b-col lg="8" md="5" sm="5">
              <router-link
                class="btn btn-success pull-right"
                :to="{
                  name: 'MasterPresentationPrintPage',
                  params: { projectNumber: project.number },
                }"
                target="_blank"
              >
                <i class="fa fa-file mr-2"></i>{{ $t('masterPresentationPreview') }}
              </router-link>
              <ButtonWait
                v-if="canBuildMasterPresentation"
                :startCallback="startMasterPresentationBuild"
                :pollCallback="pollMasterPresentationBuildState"
                class="pull-right mr-2"
              >
                <template v-slot:buttonText>
                  {{ $t('generateMasterPresentation') }}
                </template>
              </ButtonWait>
            </b-col>
          </b-row>
        </div>

        <div v-if="isShortTeaserVisible" class="presentations-part-property-image">
          <legend class="mt-3 mb-2">
            <strong>{{ $t('uploadPropertyImage') }}</strong>
          </legend>
          <b-row>
            <b-col lg="12" md="12" sm="12">
              <DropzoneUpload
                v-if="canChangeAttachments"
                class="mt-1"
                :complete="isPropertyImageUploadComplete"
                :parentId="project.id"
                :parentType="propertyImageAttachmentType"
                :uploadCallback="onUploadPropertyImages"
                acceptedFileTypes=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                :multiUpload="true"
                v-on:dropzone-upload:reset="isPropertyImageUploadComplete = false"
              />
              <AttachmentList
                :attachments="propertyImageAttachments"
                :attachmentType="propertyImageAttachmentType"
                :deletable="!isBlockReadOnly"
                :hidesDeleteForGuests="!isOrganizationRoleNavigationEnabled"
                v-on:attachment-list:delete="onDeleteAttachment"
              />
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
  </BlockFieldset>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import AttachmentList from '@/components/AttachmentList';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import ButtonWait from '@/components/ButtonWait.vue';
import DropzoneUpload from '@/components/DropzoneUpload';
import { buildStateOfType } from '@/helpers/attachmentBuildState';
import { attachmentsOfType } from '@/helpers/attachmentsOfType';
import { isMasterPresentationVisible } from '@/helpers/masterPresentationVisibility';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { PROJECT_PRESENTATIONS_FIELDS } from './projectPresentationsFragment';

/** Number of the unsaved project (ProjectEdit `_new`): the block needs a saved project. */
const NEW_PROJECT_NUMBER = '_new';

/** Match PROJECT_ATTACHMENT_TYPES on the server. */
const SHORT_TEASER_ATTACHMENT_TYPE = 'SHORT_TEASER';
const MASTER_PRESENTATION_ATTACHMENT_TYPE = 'MASTER_PRESENTATION';
const PROPERTY_IMAGE_ATTACHMENT_TYPE = 'SHORT_TEASER_PROPERTY_IMAGE';

/** Field of the block on `ProjectBlocks`. */
const PRESENTATIONS_BLOCK_FIELD = 'presentations';

/**
 * Block project-presentations (plan docs/work/_done/2026-10-project-block-presentations): generate
 * Kurzteaser and Masterpräsentation, upload Objektfotos. `project` is the block data
 * (`Project.blocks.presentations`); every operation reloads it through
 * `fetchProjectBlockByNumber`. Switch on, only the block level counts; switch off, the
 * legacy rules for guests and Sales Lead apply as in ProjectShortTeaser. The Masterpräsentation
 * is shown to E-way users only, in both modes.
 */
export default {
  name: 'ProjectPresentations',
  components: {
    AttachmentList,
    BlockFieldset,
    ButtonWait,
    DropzoneUpload,
  },
  mixins: [AccessBlockMixin],
  props: {
    project: { type: Object, required: true },
  },
  data() {
    return {
      shortTeaserAttachmentType: SHORT_TEASER_ATTACHMENT_TYPE,
      masterPresentationAttachmentType: MASTER_PRESENTATION_ATTACHMENT_TYPE,
      propertyImageAttachmentType: PROPERTY_IMAGE_ATTACHMENT_TYPE,
      isPropertyImageUploadComplete: false,
    };
  },
  computed: {
    ...mapGetters('auth', [
      'isOrganizationRoleNavigationEnabled',
      'isGuest',
      'isSalesLead',
      'getCurrentUser',
    ]),
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    /** Kurzteaser and Objektfoto; switch off hidden for Sales Lead. */
    isShortTeaserVisible() {
      return this.isOrganizationRoleNavigationEnabled || !this.isSalesLead;
    },
    isMasterPresentationVisible() {
      return isMasterPresentationVisible(this.getCurrentUser);
    },
    /** Generate and upload; switch off not for guests. */
    canChangeAttachments() {
      if (this.isBlockReadOnly) {
        return false;
      }
      return this.isOrganizationRoleNavigationEnabled || !this.isGuest;
    },
    /** Switch off additionally not for Sales Lead. */
    canBuildMasterPresentation() {
      return (
        this.canChangeAttachments && (this.isOrganizationRoleNavigationEnabled || !this.isSalesLead)
      );
    },
    shortTeaserAttachments() {
      return attachmentsOfType(this.project.attachments, SHORT_TEASER_ATTACHMENT_TYPE);
    },
    masterPresentationAttachments() {
      return attachmentsOfType(this.project.attachments, MASTER_PRESENTATION_ATTACHMENT_TYPE);
    },
    propertyImageAttachments() {
      return attachmentsOfType(this.project.attachments, PROPERTY_IMAGE_ATTACHMENT_TYPE);
    },
  },
  methods: {
    ...mapActions([
      'buildShortTeaser',
      'buildMasterPresentation',
      'fetchProjectBlockByNumber',
      'uploadProjectDynamicAttachment',
      'deleteProjectAttachment',
    ]),
    reloadBlock() {
      return this.fetchProjectBlockByNumber({
        projectNumber: this.project.number,
        blockField: PRESENTATIONS_BLOCK_FIELD,
        fragment: PROJECT_PRESENTATIONS_FIELDS,
      });
    },
    /** Build state of the type from the reloaded block, for ButtonWait. */
    async pollBuildState(attachmentType) {
      return buildStateOfType(await this.reloadBlock(), attachmentType);
    },
    async startShortTeaserBuild() {
      await this.buildShortTeaser(this.project.id);
    },
    pollShortTeaserBuildState() {
      return this.pollBuildState(SHORT_TEASER_ATTACHMENT_TYPE);
    },
    async startMasterPresentationBuild() {
      await this.buildMasterPresentation(this.project.id);
    },
    pollMasterPresentationBuildState() {
      return this.pollBuildState(MASTER_PRESENTATION_ATTACHMENT_TYPE);
    },
    async onUploadPropertyImages(payload) {
      try {
        for (const file of payload.files) {
          await this.uploadProjectDynamicAttachment({
            file,
            parentId: payload.parentId,
            parentType: payload.parentType,
          });
        }
      } finally {
        this.isPropertyImageUploadComplete = true;
        await this.reloadBlock();
      }
    },
    async onDeleteAttachment(event) {
      await this.deleteProjectAttachment({
        projectId: this.project.id,
        attachmentId: event.attachmentId,
        attachmentType: event.attachmentType,
      });
      await this.reloadBlock();
    },
  },
};
</script>
